import React, { useState, useEffect } from 'react';
import { REPORTS_DETECTION_TYPES, AREA_OPTIONS, PRIORITY_OPTIONS, CLOSE_STATUS_OPTIONS } from '../../utils/constants';
import { isAdminUser, getLoggedInArea } from '../../utils/authService';

const ReportFiltersLogic = ({ onApplyFilters, children }) => {
  
  // DEBUG: Log the imported detection types
  console.log('🔍 ReportFiltersLogic - Imported REPORTS_DETECTION_TYPES:', REPORTS_DETECTION_TYPES);
  
  // ✅ Get logged-in user info
  const userIsAdmin = isAdminUser();
  const loggedInArea = getLoggedInArea();
  
  console.log('🔐 ReportFiltersLogic - User Auth:', { isAdmin: userIsAdmin, area: loggedInArea });
  
  const getFirstArea = () => {
    // If area-specific user, force their assigned area
    if (!userIsAdmin && loggedInArea) {
      console.log('🔒 Area-specific user locked to area:', loggedInArea);
      return [loggedInArea];
    }
    
    // Admin users default to first available area
    const firstArea = AREA_OPTIONS.find(option => option.value !== "All");
    return firstArea ? [firstArea.value] : [];
  };

  const [detectionTypes, setDetectionTypes] = useState(["All"]);
  const [areas, setAreas] = useState(getFirstArea());
  const [priorities, setPriorities] = useState(["All"]);
  const [closeStatus, setCloseStatus] = useState(["All"]); // NEW: Close status filter
  const [startTime, setStartTime] = useState(new Date(new Date().setHours(0, 0, 0, 0)));
  const [endTime, setEndTime] = useState(new Date(new Date().setHours(23, 59, 59, 999)));
  
  // Track if user has made their first area selection
  const [hasUserSelectedArea, setHasUserSelectedArea] = useState(false);

  // Handle filter application
  const handleApplyFilters = () => {
    onApplyFilters({
      detectionTypes: detectionTypes,
      areas: areas,
      priorities: priorities,
      closeStatus: closeStatus, // NEW: Include close status in filters
      timeFilter: {
        startTime,
        endTime
      }
    });
  };

  // Handler for detection type change (fixed for proper multi-select)
  const handleDetectionTypeChange = (event) => {
    handleMultiSelectChangeWithAll(event, setDetectionTypes, "All");
  };

  // Generic handler for multi-select fields with "All" option
  const handleMultiSelectChangeWithAll = (event, setter, allValue = "All") => {
    const value = event.target.value;
    
    if (value.includes(allValue)) {
      const currentSelection = event.target.value;
      const wasAllSelected = currentSelection.length === 1 && currentSelection[0] === allValue;
      
      if (wasAllSelected || value.length === 1) {
        setter([allValue]);
      } else {
        setter(value.filter(v => v !== allValue));
      }
    } else if (value.length === 0) {
      setter([allValue]);
    } else {
      setter(value);
    }
  };

  // UPDATED: Enhanced area handler to support "All Areas" functionality + area restriction
  const handleAreaChange = (event) => {
    // ✅ Prevent area-specific users from changing their area
    if (!userIsAdmin && loggedInArea) {
      console.log('🔒 Area-specific user cannot change area. Locked to:', loggedInArea);
      return;
    }
    
    const value = event.target.value;
    
    // Check if "All Areas" was selected
    if (value.includes("All Areas")) {
      // If "All Areas" is being selected
      const wasAllAreasAlreadySelected = areas.includes("All Areas");
      
      if (!wasAllAreasAlreadySelected) {
        // Select all individual areas (excluding "All Areas" itself)
        const allIndividualAreas = AREA_OPTIONS
          .filter(option => option.value !== "All" && option.value !== "All Areas")
          .map(option => option.value);
        setAreas(allIndividualAreas);
        setHasUserSelectedArea(true);
        return;
      } else {
        // If "All Areas" was already selected and user clicked it again, 
        // keep all areas selected or handle as desired
        return;
      }
    }
    
    // Check if all individual areas are selected
    const allIndividualAreas = AREA_OPTIONS
      .filter(option => option.value !== "All" && option.value !== "All Areas")
      .map(option => option.value);
    
    const allIndividualAreasSelected = allIndividualAreas.every(area => value.includes(area));
    
    // If all individual areas are selected, we don't need to add "All Areas" to the state
    // The UI will show it as selected via the isSelected logic
    
    if (!hasUserSelectedArea && value.length > 0) {
      setHasUserSelectedArea(true);
      
      const defaultArea = getFirstArea()[0];
      
      if (value.includes(defaultArea) && value.length > 1) {
        const newSelection = value.filter(v => v !== defaultArea && v !== "All Areas");
        setAreas(newSelection.length > 0 ? newSelection : [defaultArea]);
      } else {
        const cleanedSelection = value.filter(v => v !== "All Areas");
        setAreas(cleanedSelection.length > 0 ? cleanedSelection : allIndividualAreas);
      }
    } else {
      if (value.length === 0) {
        setAreas(getFirstArea());
      } else {
        // Remove "All Areas" from the actual state since we track individual areas
        const cleanedSelection = value.filter(v => v !== "All Areas");
        setAreas(cleanedSelection.length > 0 ? cleanedSelection : allIndividualAreas);
      }
    }
  };

  const handlePriorityChange = (event) => {
    handleMultiSelectChangeWithAll(event, setPriorities, "All");
  };

  // NEW: Handler for close status change
  const handleCloseStatusChange = (event) => {
    handleMultiSelectChangeWithAll(event, setCloseStatus, "All");
  };

  const getDetectionTypeLabel = (value) => {
    if (value === "All") return "All Types";
    const found = REPORTS_DETECTION_TYPES.find(dt => dt.id === value);
    return found ? found.title : value;
  };

  const getAreaLabel = (value) => {
    if (value === "All") return "All Areas";
    if (value === "All Areas") return "All Areas";
    const found = AREA_OPTIONS.find(area => area.value === value);
    return found ? found.label : value;
  };

  // Get label for a priority value
  const getPriorityLabel = (value) => {
    if (value === "All") return "All Priorities";
    const found = PRIORITY_OPTIONS.find(priority => priority.value === value);
    return found ? found.label : value;
  };

  // NEW: Get label for a close status value
  const getCloseStatusLabel = (value) => {
    if (value === "All") return "All Events";
    const found = CLOSE_STATUS_OPTIONS.find(status => status.value === value);
    return found ? found.label : value;
  };

  // UPDATED: Enhanced selection logic for areas with "All Areas" support
  const isSelected = (array, value) => {
    // For area selection with "All Areas" logic
    if (value === "All Areas") {
      // "All Areas" is considered selected if all individual areas are selected
      const allIndividualAreas = AREA_OPTIONS
        .filter(option => option.value !== "All" && option.value !== "All Areas")
        .map(option => option.value);
      return allIndividualAreas.every(area => array.includes(area));
    }
    
    // If "All" is selected for other filters, then all individual items should appear checked
    if (array.includes("All") && value !== "All") {
      return true;
    }
    return array.indexOf(value) > -1;
  };

  // UPDATED: Enhanced area options with "All Areas" 
  const getAreaOptionsForUI = () => {
    // Add "All Areas" option at the top, then regular areas (excluding original "All")
    const regularAreas = AREA_OPTIONS.filter(option => option.value !== "All");
    return [
      { value: "All Areas", label: "All Areas" },
      ...regularAreas
    ];
  };

  const filterData = {
    detectionTypes,
    areas,
    priorities,
    closeStatus, // NEW: Include close status in filter data
    startTime,
    endTime,
    DETECTION_TYPES: REPORTS_DETECTION_TYPES, // Use the filtered list for reports
    AREA_OPTIONS: getAreaOptionsForUI(), // Use the enhanced area options
    PRIORITY_OPTIONS,
    CLOSE_STATUS_OPTIONS // NEW: Include close status options
  };

  // DEBUG: Log the filterData that will be passed to the UI
  console.log('🔍 ReportFiltersLogic - FilterData DETECTION_TYPES being passed:', filterData.DETECTION_TYPES);

  const handlers = {
    handleDetectionTypeChange,
    handleAreaChange,
    handlePriorityChange,
    handleCloseStatusChange, // NEW: Include close status handler
    setStartTime,
    setEndTime,
    handleApplyFilters,
    getDetectionTypeLabel,
    getAreaLabel,
    getPriorityLabel,
    getCloseStatusLabel, // NEW: Include close status label getter
    isSelected
  };

  return children({ filterData, handlers });
};

export { ReportFiltersLogic };